/**
 * Cross-project construction files browser.
 *
 * Rendered inside the sidebar `Dosyalar → İnşaat Dosyaları` tab; loads
 * every construction file across every project the tenant owns and
 * offers a project rail (left) + category chips (top) + search to find
 * a specific asset without visiting each project's detail page.
 *
 * Deliberately self-contained — the per-project Files tab in
 * `CopConstructionTrackingDetail` handles uploads / deletes / edits;
 * this is a read + preview + deep-link surface.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  Folder,
  Download,
  Eye,
  FileText,
  X,
  Layers,
  ExternalLink,
  CheckSquare,
  Square,
  Archive,
  CheckCheck,
  Share2,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { constructionApi } from "../../lib/copApi";
import {
  FILE_CATEGORIES,
  CATEGORY_META,
  bucketOf,
  humanFileSize,
} from "../../lib/constructionFileMeta";
import ShareLinkDialog from "../../components/construction/ShareLinkDialog";

function formatRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.round(diff / 1000);
  if (sec < 45) return "az önce";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}dk`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}sa`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d}g`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}h`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}ay`;
  return `${Math.round(d / 365)}y`;
}

export default function CopFilesConstruction() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState("all");     // construction_id or 'all'
  const [categoryFilter, setCategoryFilter] = useState("all");    // bucket key or 'all'
  const [q, setQ] = useState("");
  const [thumbUrls, setThumbUrls] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [shareFile, setShareFile] = useState(null); // opens the share-link dialog
  // Multi-select for bulk download. Storing the ids in a Set keeps the
  // toggle O(1); the array form is only rebuilt at API-call time.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await constructionApi.allFiles();
        setFiles(data || []);
      } catch (err) {
        console.error(err);
        toast.error("Dosyalar yüklenemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Fetch image thumbnails as blob URLs. Same pattern as the detail
  // page — auth-protected download endpoint, so we can't just point
  // `<img src>` at it directly.
  useEffect(() => {
    let cancelled = false;
    const images = files.filter((f) => (f.content_type || "").startsWith("image/"));
    (async () => {
      const next = {};
      for (const f of images) {
        try {
          const res = await api.get(
            `/construction-files/${f.id}/download`,
            { responseType: "blob" }
          );
          if (cancelled) return;
          next[f.id] = URL.createObjectURL(res.data);
        } catch { /* skip failed thumb */ }
      }
      if (!cancelled) {
        setThumbUrls((prev) => {
          for (const url of Object.values(prev)) {
            try { URL.revokeObjectURL(url); } catch { /* noop */ }
          }
          return next;
        });
      }
    })();
    return () => { cancelled = true; };
  }, [files]);

  useEffect(() => {
    return () => {
      setThumbUrls((prev) => {
        for (const url of Object.values(prev)) {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
        }
        return {};
      });
    };
  }, []);

  // Roll-up: { construction_id: { name, standNo, status, stage, count,
  // totalBytes } } for the left rail. Status defaults to "gray" (no
  // state set); totalBytes is summed across every file so the rail can
  // show "1.2 MB" next to the file count.
  const projects = useMemo(() => {
    const m = new Map();
    for (const f of files) {
      const pid = f.construction_id;
      if (!pid) continue;
      const cur = m.get(pid) || {
        id: pid,
        name: f.construction_company_name || "—",
        standNo: f.construction_stand_no || "",
        status: f.construction_status || "gray",
        stage: f.construction_stage || "",
        count: 0,
        totalBytes: 0,
      };
      cur.count += 1;
      cur.totalBytes += f.size || 0;
      m.set(pid, cur);
    }
    return Array.from(m.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "tr")
    );
  }, [files]);

  // Aggregate size across ALL files (regardless of project) for the
  // "Tüm katılımcılar" row's sub-line.
  const totalBytesAll = useMemo(
    () => files.reduce((s, f) => s + (f.size || 0), 0),
    [files]
  );

  // Category counts (respects the current project filter so users see
  // "3 dosya" and get exactly 3 files when they click).
  const projectScoped = useMemo(
    () =>
      projectFilter === "all"
        ? files
        : files.filter((f) => f.construction_id === projectFilter),
    [files, projectFilter]
  );

  const categoryCounts = useMemo(() => {
    const c = { all: projectScoped.length };
    for (const cat of FILE_CATEGORIES) c[cat.key] = 0;
    for (const f of projectScoped) c[bucketOf(f.category)] += 1;
    return c;
  }, [projectScoped]);

  const shown = useMemo(() => {
    let list = projectScoped;
    if (categoryFilter !== "all") {
      list = list.filter((f) => bucketOf(f.category) === categoryFilter);
    }
    if (q.trim()) {
      const rx = q.trim().toLowerCase();
      list = list.filter(
        (f) =>
          (f.original_filename || "").toLowerCase().includes(rx) ||
          (f.construction_company_name || "").toLowerCase().includes(rx)
      );
    }
    return list;
  }, [projectScoped, categoryFilter, q]);

  const downloadFile = useCallback(async (file) => {
    try {
      const res = await api.get(`/construction-files/${file.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    }
  }, []);

  // --- Selection helpers -------------------------------------------
  const toggleSelected = useCallback((fileId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  // When the visible slice changes we prune the selection to what's
  // actually on screen, so hitting "Tümünü seç" always toggles the
  // filtered set the user is looking at.
  const selectAllShown = useCallback((shownList) => {
    setSelectedIds((prev) => {
      const shownIds = shownList.map((f) => f.id);
      const allSelected = shownIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        shownIds.forEach((id) => next.delete(id));
      } else {
        shownIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const bulkDownload = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (ids.length > 100) {
      toast.error("En fazla 100 dosya seçilebilir");
      return;
    }
    setBulkDownloading(true);
    try {
      const res = await constructionApi.bulkDownloadFiles(ids);
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      // Prefer the Content-Disposition filename if present (server
      // stamps a date-based name); fall back to a generic one.
      let filename = "insaat-dosyalar.zip";
      const disp = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(disp);
      if (m) filename = m[1];
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const count = res.headers?.["x-file-count"] || ids.length;
      toast.success(`${count} dosya ZIP olarak indirildi`);
      clearSelection();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Toplu indirme başarısız";
      toast.error(String(msg));
    } finally {
      setBulkDownloading(false);
    }
  }, [selectedIds, clearSelection]);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]" data-testid="cop-files-construction-page">
      {/* --- Left rail: project navigator --- */}
      <aside
        className="flex max-h-[70vh] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white"
        data-testid="cf-project-rail"
      >
        <div className="border-b border-zinc-100 px-3 py-2.5">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            <Layers className="h-3.5 w-3.5" /> Katılımcılar
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <ProjectRailButton
            active={projectFilter === "all"}
            onClick={() => setProjectFilter("all")}
            label="Tüm katılımcılar"
            sub={files.length > 0 ? humanFileSize(totalBytesAll) : ""}
            count={files.length}
            testid="cf-project-all"
          />
          <div className="my-1 h-px bg-zinc-100" />
          {projects.length === 0 ? (
            <div className="px-3 py-6 text-center text-[11px] text-zinc-400">
              Henüz katılımcı yok.
            </div>
          ) : (
            projects.map((p) => {
              // Compose the sub-line: "#StandNo · Stage · Size". Each
              // piece is optional — omit gracefully when empty so we
              // don't leave dangling separators.
              const parts = [];
              if (p.standNo) parts.push(`#${p.standNo}`);
              if (p.stage) parts.push(p.stage);
              if (p.totalBytes > 0) parts.push(humanFileSize(p.totalBytes));
              return (
                <ProjectRailButton
                  key={p.id}
                  active={projectFilter === p.id}
                  onClick={() => setProjectFilter(p.id)}
                  label={p.name}
                  sub={parts.join(" · ")}
                  status={p.status}
                  count={p.count}
                  testid={`cf-project-${p.id}`}
                />
              );
            })
          )}
        </div>
      </aside>

      {/* --- Right main content --- */}
      <section className="min-w-0 space-y-4">
        {/* Selection toolbar — appears only when 1+ file is selected.
            Sticky-ish placement above chips so the user never loses
            sight of it while scrolling the grid. */}
        {selectedIds.size > 0 && (
          <div
            className="flex flex-wrap items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2"
            data-testid="cf-selection-toolbar"
          >
            <span
              className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-2.5 py-1 text-[11px] font-semibold text-white"
              data-testid="cf-selection-count"
            >
              <CheckCheck className="h-3 w-3" />
              {selectedIds.size} dosya seçildi
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={clearSelection}
                data-testid="cf-selection-clear"
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                Temizle
              </Button>
              <Button
                onClick={bulkDownload}
                disabled={bulkDownloading}
                size="sm"
                data-testid="cf-bulk-download"
                className="bg-violet-600 text-white hover:bg-violet-700"
              >
                {bulkDownloading ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Archive className="mr-1.5 h-3.5 w-3.5" />
                )}
                ZIP olarak indir
              </Button>
            </div>
          </div>
        )}

        {/* Category chips + search */}
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-white p-3">
          <CategoryChip
            active={categoryFilter === "all"}
            onClick={() => setCategoryFilter("all")}
            Icon={Folder}
            label="Tümü"
            count={categoryCounts.all}
            testid="cf-cat-all"
            tint="bg-zinc-900 text-white"
            inactiveTint="bg-zinc-100 text-zinc-700"
          />
          {FILE_CATEGORIES.map((c) => (
            <CategoryChip
              key={c.key}
              active={categoryFilter === c.key}
              onClick={() => setCategoryFilter(c.key)}
              Icon={c.Icon}
              label={c.label}
              count={categoryCounts[c.key] || 0}
              testid={`cf-cat-${c.key}`}
              tint="bg-zinc-900 text-white"
              inactiveTint={c.tint}
            />
          ))}
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Dosya adı veya proje…"
              className="pl-9"
              data-testid="cf-search"
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center rounded-xl border border-zinc-200 bg-white py-24">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        ) : shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-16 text-center text-sm text-zinc-500">
            {files.length === 0
              ? "Henüz hiçbir projeye dosya yüklenmemiş."
              : "Filtreye uyan dosya yok."}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="text-[12px] text-zinc-500">
                {shown.length} dosya gösteriliyor
              </div>
              {shown.length > 0 && (
                <button
                  type="button"
                  onClick={() => selectAllShown(shown)}
                  data-testid="cf-select-all"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                >
                  {shown.every((f) => selectedIds.has(f.id)) ? (
                    <><CheckSquare className="h-3.5 w-3.5 text-violet-600" /> Tümünün seçimini kaldır</>
                  ) : (
                    <><Square className="h-3.5 w-3.5" /> Tümünü seç</>
                  )}
                </button>
              )}
            </div>
            <ul
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              data-testid="cf-files-list"
            >
              {shown.map((f) => (
                <CrossFileCard
                  key={f.id}
                  file={f}
                  thumbUrl={thumbUrls[f.id]}
                  selected={selectedIds.has(f.id)}
                  onToggleSelect={() => toggleSelected(f.id)}
                  onDownload={() => downloadFile(f)}
                  onPreview={() => setPreviewFile(f)}
                  onShare={() => setShareFile(f)}
                />
              ))}
            </ul>
          </>
        )}
      </section>

      <FilePreviewModal
        file={previewFile}
        thumbUrl={previewFile ? thumbUrls[previewFile.id] : null}
        onClose={() => setPreviewFile(null)}
        onDownload={downloadFile}
      />

      <ShareLinkDialog
        file={shareFile}
        onClose={() => setShareFile(null)}
      />
    </div>
  );
}

// Traffic-light color for the status dot rendered inside ProjectRailButton.
// Mirrors the Construction row status → same tokens as the tracker page.
const STATUS_DOT_COLOR = {
  green: "bg-emerald-500",
  yellow: "bg-amber-400",
  red: "bg-red-500",
  gray: "bg-zinc-300",
};

const STATUS_DOT_LABEL = {
  green: "Yolunda",
  yellow: "Dikkat",
  red: "Gecikme / risk",
  gray: "Durum yok",
};

function ProjectRailButton({ active, onClick, label, sub, status, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors ${
        active
          ? "bg-zinc-900 text-white"
          : "text-zinc-700 hover:bg-zinc-50"
      }`}
    >
      {status !== undefined && (
        <span
          aria-hidden
          title={STATUS_DOT_LABEL[status] || ""}
          className={`h-2 w-2 shrink-0 rounded-full ${
            STATUS_DOT_COLOR[status] || STATUS_DOT_COLOR.gray
          } ${status === "gray" && active ? "opacity-60" : ""}`}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate">{label}</div>
        {sub && (
          <div className={`truncate text-[10px] ${active ? "text-white/70" : "text-zinc-400"}`}>
            {sub}
          </div>
        )}
      </div>
      <span
        className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
          active ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-600"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function CategoryChip({ active, onClick, Icon, label, count, testid, tint, inactiveTint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors ${
        active ? tint : `${inactiveTint} hover:brightness-95`
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
          active ? "bg-white/20 text-white" : "bg-white/60 text-zinc-800"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function CrossFileCard({ file, thumbUrl, onDownload, onPreview, onShare, selected, onToggleSelect }) {
  const cat = CATEGORY_META[bucketOf(file.category)] || CATEGORY_META.other;
  const Icon = cat.Icon;
  const isImage = (file.content_type || "").startsWith("image/");
  const isPdf = file.content_type === "application/pdf";

  return (
    <li
      data-testid={`cf-card-${file.id}`}
      data-selected={selected ? "true" : "false"}
      className={`group relative flex flex-col overflow-hidden rounded-xl border bg-white transition-all hover:-translate-y-0.5 hover:shadow-md ${
        selected
          ? "border-violet-400 ring-2 ring-violet-200"
          : "border-zinc-200 hover:border-violet-300"
      }`}
    >
      {/* Selection checkbox — visible on hover, or always when the
          card is already selected. Sits above the thumbnail. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        data-testid={`cf-select-${file.id}`}
        aria-label={selected ? "Seçimi kaldır" : "Dosyayı seç"}
        className={`absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border transition-all ${
          selected
            ? "border-violet-500 bg-violet-600 text-white opacity-100 shadow"
            : "border-zinc-300 bg-white/90 text-zinc-500 opacity-0 backdrop-blur group-hover:opacity-100 hover:border-violet-400 hover:text-violet-600"
        }`}
      >
        {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
      </button>

      <div className={`flex items-center gap-2 px-3 py-1.5 pl-10 text-[10px] font-semibold ${cat.tint}`}>
        <Icon className="h-3 w-3" />
        <span className="uppercase tracking-wide">{cat.label}</span>
        <span className="ml-auto text-[10px] font-normal opacity-70">
          {humanFileSize(file.size)}
        </span>
      </div>

      <button
        type="button"
        onClick={onPreview}
        data-testid={`cf-preview-${file.id}`}
        disabled={!isImage && !isPdf}
        className="relative flex h-36 w-full items-center justify-center overflow-hidden bg-zinc-50 disabled:cursor-default"
      >
        {isImage && thumbUrl ? (
          <img
            src={thumbUrl}
            alt={file.original_filename}
            className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
          />
        ) : isImage ? (
          <Loader2 className="h-6 w-6 animate-spin text-zinc-300" />
        ) : (
          <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${cat.tint}`}>
            {isPdf ? <FileText className="h-7 w-7" /> : <Icon className="h-7 w-7" />}
          </div>
        )}
        {(isImage || isPdf) && (
          <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">
            <Eye className="h-3 w-3" /> Önizle
          </span>
        )}
      </button>

      <div className="px-3 py-2.5">
        <div className="truncate text-[13px] font-medium text-zinc-900" title={file.original_filename}>
          {file.original_filename}
        </div>
        <div className="mt-0.5 text-[11px] text-zinc-500">
          {file.uploaded_by_name} · {formatRelative(file.created_at)}
        </div>
        {file.construction_company_name && (
          <Link
            to={`/app/cop/construction-tracking/${file.construction_id}`}
            data-testid={`cf-project-link-${file.id}`}
            className="mt-2 inline-flex max-w-full items-center gap-1 truncate rounded-md bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-800 hover:bg-violet-50 hover:text-violet-700"
          >
            <span className="truncate">{file.construction_company_name}</span>
            {file.construction_stand_no && (
              <span className="shrink-0 text-zinc-500">#{file.construction_stand_no}</span>
            )}
            <ExternalLink className="ml-0.5 h-3 w-3 shrink-0 opacity-50" />
          </Link>
        )}
      </div>

      <div className="mt-auto flex border-t border-zinc-100">
        <button
          type="button"
          onClick={onDownload}
          data-testid={`cf-download-${file.id}`}
          className="flex flex-1 items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
        >
          <Download className="h-3.5 w-3.5" /> İndir
        </button>
        <div className="w-px bg-zinc-100" />
        <button
          type="button"
          onClick={onShare}
          data-testid={`cf-share-${file.id}`}
          className="flex flex-1 items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-violet-600 transition-colors hover:bg-violet-50"
        >
          <Share2 className="h-3.5 w-3.5" /> Paylaş
        </button>
      </div>
    </li>
  );
}

function FilePreviewModal({ file, thumbUrl, onClose, onDownload }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const isImage = file && (file.content_type || "").startsWith("image/");
  const isPdf = file && file.content_type === "application/pdf";

  useEffect(() => {
    let cancelled = false;
    if (!file || !isPdf) {
      setPdfUrl(null);
      return () => {};
    }
    setPdfLoading(true);
    (async () => {
      try {
        const res = await api.get(`/construction-files/${file.id}/download`, {
          responseType: "blob",
        });
        if (cancelled) return;
        setPdfUrl(URL.createObjectURL(res.data));
      } catch {
        toast.error("Önizleme yüklenemedi");
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setPdfUrl((prev) => {
        if (prev) try { URL.revokeObjectURL(prev); } catch { /* noop */ }
        return null;
      });
    };
  }, [file, isPdf]);

  const cat = file
    ? CATEGORY_META[bucketOf(file.category)] || CATEGORY_META.other
    : null;

  return (
    <Dialog open={!!file} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="cf-preview-modal"
        className="flex h-[85vh] max-w-6xl flex-col gap-0 overflow-hidden p-0"
      >
        {file && (
          <>
            <div className="flex items-center gap-3 border-b border-zinc-200 bg-white px-4 py-3">
              <span className={`flex h-8 w-8 items-center justify-center rounded-md ${cat.tint}`}>
                <cat.Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <DialogTitle className="truncate text-sm font-semibold text-zinc-900">
                  {file.original_filename}
                </DialogTitle>
                <div className="text-[11px] text-zinc-500">
                  {cat.label} · {humanFileSize(file.size)} · {file.uploaded_by_name}
                  {file.construction_company_name && (
                    <>
                      {" · "}
                      <Link
                        to={`/app/cop/construction-tracking/${file.construction_id}`}
                        onClick={onClose}
                        className="text-violet-600 hover:text-violet-800"
                      >
                        {file.construction_company_name}
                        {file.construction_stand_no ? ` #${file.construction_stand_no}` : ""}
                      </Link>
                    </>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownload(file)}
                data-testid="cf-preview-download"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> İndir
              </Button>
              <button
                type="button"
                onClick={onClose}
                data-testid="cf-preview-close"
                className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                aria-label="Kapat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-1 items-center justify-center overflow-auto bg-zinc-950 p-4">
              {isImage && thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt={file.original_filename}
                  data-testid="cf-preview-image"
                  className="max-h-full max-w-full object-contain shadow-2xl"
                />
              ) : isPdf ? (
                pdfLoading || !pdfUrl ? (
                  <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
                ) : (
                  <iframe
                    src={pdfUrl}
                    title={file.original_filename}
                    data-testid="cf-preview-pdf"
                    className="h-full w-full rounded-md bg-white"
                  />
                )
              ) : (
                <div className="flex flex-col items-center gap-3 text-center text-zinc-300">
                  <div className={`flex h-20 w-20 items-center justify-center rounded-2xl ${cat.tint}`}>
                    <cat.Icon className="h-10 w-10" />
                  </div>
                  <div className="text-sm font-medium text-white">
                    Bu dosya türü tarayıcıda önizlenemiyor.
                  </div>
                  <div className="text-[12px] text-zinc-400">
                    İndir düğmesini kullanarak inceleyebilirsin.
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
